import React, { useState, useEffect } from 'react';
import ExifReader from 'exifreader';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import DropZone from './DropZone';
import { analyzeForensics } from '../utils/forensics';
import { clsx } from 'clsx';

const MetadataDiff = ({ tagsA, tagsB }) => {
  const allKeys = Array.from(new Set([...Object.keys(tagsA || {}), ...Object.keys(tagsB || {})]))
    .filter(k => !['MakerNote', 'UserComment', 'Thumbnail'].includes(k))
    .sort();

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <SafeIcon icon={FiIcons.FiColumns} className="text-blue-500" /> Metadata Comparison
        </h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-left">
          <thead className="bg-gray-50 text-gray-500 uppercase">
            <tr>
              <th className="px-6 py-3">Tag</th>
              <th className="px-6 py-3">Image A</th>
              <th className="px-6 py-3">Image B</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {allKeys.map(key => {
              const valA = tagsA?.[key]?.description || tagsA?.[key]?.value?.toString() || '-';
              const valB = tagsB?.[key]?.description || tagsB?.[key]?.value?.toString() || '-';
              const isDifferent = valA !== valB;

              return (
                <tr key={key} className={clsx("hover:bg-gray-50/50", isDifferent && "bg-amber-50/30")}>
                  <td className="px-6 py-3 font-mono text-[10px] font-bold text-gray-500">{key}</td>
                  <td className={clsx("px-6 py-3 break-all", isDifferent ? "text-amber-700 font-medium" : "text-gray-600")}>{valA}</td>
                  <td className={clsx("px-6 py-3 break-all", isDifferent ? "text-amber-700 font-medium" : "text-gray-600")}>{valB}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CompareTool = () => {
  const [imgA, setImgA] = useState(null);
  const [imgB, setImgB] = useState(null);
  const [sliderPos, setSliderPos] = useState(50);

  const handleFile = async (file, side) => {
    const tags = await ExifReader.load(file);
    const report = analyzeForensics(file, tags);
    const data = {
      file,
      tags,
      report,
      url: URL.createObjectURL(file)
    };
    if (side === 'A') setImgA(data);
    else setImgB(data);
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {!imgA ? (
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase text-gray-400 tracking-widest">Image A (Reference)</label>
            <DropZone onFileSelect={(f) => handleFile(f, 'A')} />
          </div>
        ) : (
          <div className="relative group">
            <img src={imgA.url} className="w-full aspect-video object-cover rounded-xl border border-gray-200" alt="A" />
            <button 
              onClick={() => setImgA(null)}
              className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
            >
              <SafeIcon icon={FiIcons.FiX} />
            </button>
            <div className="mt-2 flex justify-between items-center px-1">
              <span className="text-xs font-bold text-blue-600">IMAGE A</span>
              <span className="text-[10px] text-gray-400">{imgA.file.name}</span>
            </div>
          </div>
        )}

        {!imgB ? (
          <div className="space-y-2">
            <label className="text-xs font-bold uppercase text-gray-400 tracking-widest">Image B (Target)</label>
            <DropZone onFileSelect={(f) => handleFile(f, 'B')} />
          </div>
        ) : (
          <div className="relative group">
            <img src={imgB.url} className="w-full aspect-video object-cover rounded-xl border border-gray-200" alt="B" />
            <button 
              onClick={() => setImgB(null)}
              className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition"
            >
              <SafeIcon icon={FiIcons.FiX} />
            </button>
            <div className="mt-2 flex justify-between items-center px-1">
              <span className="text-xs font-bold text-purple-600">IMAGE B</span>
              <span className="text-[10px] text-gray-400">{imgB.file.name}</span>
            </div>
          </div>
        )}
      </div>

      {imgA && imgB && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
          {/* Visual Slider Comparison */}
          <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiLayers} className="text-blue-500" /> Visual Overlay Inspector
            </h3>
            <div className="relative w-full aspect-video rounded-xl overflow-hidden cursor-ew-resize select-none border border-gray-100">
              {/* Image B (Bottom) */}
              <img src={imgB.url} className="absolute inset-0 w-full h-full object-contain" alt="B" />
              
              {/* Image A (Top with Clip) */}
              <div 
                className="absolute inset-0 w-full h-full overflow-hidden"
                style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}
              >
                <img src={imgA.url} className="w-full h-full object-contain" alt="A" />
              </div>

              {/* Slider Handle */}
              <div 
                className="absolute inset-y-0 w-1 bg-white shadow-[0_0_10px_rgba(0,0,0,0.3)] z-10"
                style={{ left: `${sliderPos}%` }}
              >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-400">
                  <SafeIcon icon={FiIcons.FiChevronsLeft} className="text-xs" />
                  <SafeIcon icon={FiIcons.FiChevronsRight} className="text-xs" />
                </div>
              </div>

              <input 
                type="range" 
                min="0" 
                max="100" 
                value={sliderPos} 
                onChange={(e) => setSliderPos(e.target.value)}
                className="absolute inset-0 w-full h-full opacity-0 z-20 cursor-ew-resize"
              />
            </div>
            <p className="text-center text-[10px] text-gray-400 mt-4 uppercase tracking-widest font-bold">
              Drag slider to compare pixel alignment and color grading
            </p>
          </div>

          <MetadataDiff tagsA={imgA.tags} tagsB={imgB.tags} />
        </motion.div>
      )}
    </div>
  );
};

export default CompareTool;